// NPM
import { instances } from '@/services/http';
import type { HelpeeSignupFormData } from './api.constants';
// (Interceptors and error handling moved to services/http.ts)
import { useUserStore } from '@/stores/user';
import type {
  GenericLoginGetRequestType,
  GenericLoginGetResponseType,
  ProspectDashboardGetCampaignInfoResponseType,
  ProspectDashboardGetJwtTokenRequestType,
  ProspectDashboardGetJwtTokenResponseType,
  ProspectDashboardGetProspectTouchPointsResponseType,
  ProspectDashboardGetRelationRequestType,
  ProspectDashboardGetRelationResponseType,
  ProspectDashboardGetSupportAdminResponseType,
  ProspectDashboardPostRelationMomentDoneRequestType,
  ProspectDashboardPostRelationMomentDoneResponseType,
  ProspectDashboardPostRelationMomentOngoingRequestType,
  ProspectDashboardPostRelationMomentOngoingResponseType,
  ProspectDashboardPostRelationReportResponseType,
  ProspectDashboardPostSupportMessageRequestType,
  ProspectDashboardPostSupportMessageResponseType,
  ProspectDashboardPutProspectTouchPointsRequestType,
  ProspectDashboardPutProspectTouchPointsResponseType,
  ReviewsCreatePublicReviewRequestType,
  ReviewsCreatePublicReviewResponseType,
  ReviewsCreateReviewRequestType,
  ReviewsCreateReviewResponseType,
  ReviewsGetPublicReviewStatusRequestType,
  ReviewsGetPublicReviewStatusResponseType,
  ReviewsGetReviewStatusRequestType,
  ReviewsGetReviewStatusResponseType,
  SharingGetSharingDataRequestType,
  SharingGetSharingDataResponseType,
  SignUpProspectGetQualificationRequestType,
  SignUpProspectGetQualificationResponseType,
  SignUpProspectGetReferredRequestType,
  SignUpProspectGetReferredResponseType,
  SignUpProspectPostEmailRequestType,
  SignUpProspectPostEmailResponseType,
  SignUpProspectPostReferredRequestType,
  SignUpProspectPostReferredResponseType,
  SignUpProspectPostSharingRequestType,
  SignUpProspectPostSharingResponseType,
  SignUpProspectSendCodeRequestType,
  SignUpProspectSendCodeResponseType,
  SignUpProspectUpdateExpectationRequestType,
  SignUpProspectUpdateExpectationResponseType,
  SignUpProspectUpdateOtherCriteriaRequestType,
  SignUpProspectUpdateOtherCriteriaResponseType,
  SignUpProspectUpdateProspectMainInformationRequestType,
  SignUpProspectUpdateProspectMainInformationResponseType,
  SignUpProspectUpdateTouchPointsRequestType,
  SignUpProspectUpdateTouchPointsResponseType,
  SignUpProspectVerifyCodeRequestType,
  SignUpProspectVerifyCodeResponseType,
  UserDashboardGetAllChallengesResponseType,
  UserDashboardGetAllEventsResponseType,
  UserDashboardGetAllUgcsResponseType,
  UserDashboardGetCampaignInfoResponseType,
  UserDashboardGetHelperCampaignsResponseType,
  UserDashboardGetHelperSignedUpResponseType,
  UserDashboardGetPurchasedRewardsResponseType,
  UserDashboardGetRewardsResponseType,
  UserDashboardGetSharingResponseType,
  UserDashboardGetUserCriteriaResponseType,
  UserDashboardGetUserResponseType,
  UserDashboardGetUserTouchPointsResponseType,
  UserDashboardJoinCampaignHelperRequestType,
  UserDashboardJoinCampaignHelperResponseType,
  UserDashboardJwtTokenRequestType,
  UserDashboardJwtTokenResponseType,
  UserDashboardLeaveCampaignHelperRequestType,
  UserDashboardLeaveCampaignHelperResponseType,
  UserDashboardPostMessageRequestType,
  UserDashboardPostMessageResponseType,
  UserDashboardPurchaseRewardRequestType,
  UserDashboardPurchaseRewardResponseType,
  UserDashboardReferSomeonePostRequestType,
  UserDashboardReferSomeonePostResponseType,
  UserDashboardRelationMomentPostDoneRequestType,
  UserDashboardRelationMomentPostDoneResponseType,
  UserDashboardRelationMomentPostMessageRequestType,
  UserDashboardRelationMomentPostMessageResponseType,
  UserDashboardRelationMomentPostOngoingRequestType,
  UserDashboardRelationMomentPostOngoingResponseType,
  UserDashboardRelationMomentPostReadRequestType,
  UserDashboardRelationMomentPostReadResponseType,
  UserDashboardRelationsGetHelperRelationDetailsRequestType,
  UserDashboardRelationsGetHelperRelationDetailsResponseType,
  UserDashboardRelationsGetHelperRelationsResponseType,
  UserDashboardRelationsPostHelperReportHelpeeRequestType,
  UserDashboardRelationsPostHelperReportHelpeeResponseType,
  UserDashboardStartChallengeRequestType,
  UserDashboardStartChallengeResponseType,
  UserDashboardStartUgcRequestType,
  UserDashboardStartUgcResponseType,
  UserDashboardUpdateCampaignHelperSettingsRequestType,
  UserDashboardUpdateCampaignHelperSettingsResponseType,
  UserDashboardUpdateUserCriteriaRequestType,
  UserDashboardUpdateUserCriteriaResponseType,
  UserDashboardUpdateUserProfilePictureRequestType,
  UserDashboardUpdateUserProfilePictureResponseType,
  UserDashboardUpdateUserRequestType,
  UserDashboardUpdateUserResponseType,
  UserDashboardUpdateUserTouchPointRequestType,
  UserDashboardUpdateUserTouchPointResponseType,
  UserDashboardValidateChallengeRequestType,
  UserDashboardValidateChallengeResponseType,
  UserDashboardValidateUgcRequestType,
  UserDashboardValidateUgcResponseType,
} from '@magma-app/magma-types/all';
import type { UserDashboardGetAdminResponseType } from '@magma-app/magma-types/user-dashboard/support';

const serializeFormData = (payload: {
  [key: string]: string | number | FileList | File;
}) => {
  const formData = new FormData();

  for (const key in payload) {
    if (typeof payload[key] === 'string' || typeof payload[key] === 'number') {
      formData.append(key, `${payload[key]}`);
    } else if (payload[key] instanceof FileList) {
      for (const file of payload[key] as FileList) {
        formData.append(key, file);
      }
    } else if (payload[key] instanceof File) {
      formData.append(key, payload[key] as File);
    } else {
      formData.append(key, JSON.stringify(payload[key]));
    }
  }

  return formData;
};

// Interceptors are centralized in services/http.ts

// Export the API factory for each endpoint
export default class Api {
  public static main() {
    return {
      auth: {
        signInAs: async (
          payload: GenericLoginGetRequestType
        ): Promise<{
          data: GenericLoginGetResponseType;
        }> => {
          const params = new URLSearchParams({
            email: payload.email,
          });
          if (payload.loginAs) params.set('loginAs', payload.loginAs);
          if (payload.organizationUuid)
            params.set('organizationUuid', payload.organizationUuid);
          if (payload.redirect) params.set('redirect', payload.redirect);

          return await instances.main.get(`/auth/login?${params.toString()}`);
        },

        signIn: async (
          payload: GenericLoginGetRequestType
        ): Promise<{
          data: GenericLoginGetResponseType;
        }> => {
          const params = new URLSearchParams({ email: payload.email });
          if (payload.redirect) params.set('redirect', payload.redirect);

          return await instances.main.get(`/auth/login?${params.toString()}`);
        },

        signUpHelpee: async (
          payload: SignUpProspectPostEmailRequestType,
          turnstileToken?: string
        ): Promise<{
          data: SignUpProspectPostEmailResponseType;
        }> => {
          return await instances.main.post('/sign-up-prospect/email', payload, {
            ...(turnstileToken && {
              headers: {
                'x-turnstile-token': turnstileToken,
              },
            }),
          });
        },

        sendCodeRequest: async (
          payload: SignUpProspectSendCodeRequestType
        ): Promise<{
          data: SignUpProspectSendCodeResponseType;
        }> => {
          return await instances.main.post(
            '/sign-up-prospect/phone/send-code',
            payload
          );
        },

        validateCode: async (
          payload: SignUpProspectVerifyCodeRequestType
        ): Promise<{
          data: SignUpProspectVerifyCodeResponseType;
        }> => {
          return await instances.main.post(
            '/sign-up-prospect/phone/verify-code',
            payload
          );
        },

        completeSignUpHelpee: async (
          payload: SignUpProspectUpdateProspectMainInformationRequestType
        ): Promise<{
          data: SignUpProspectUpdateProspectMainInformationResponseType;
        }> => {
          return await instances.main.put(
            '/sign-up-prospect/main-information',
            payload
          );
        },

        addOptionalInfoSignUpHelpee: async (
          payload: SignUpProspectUpdateOtherCriteriaRequestType
        ): Promise<{
          data: SignUpProspectUpdateOtherCriteriaResponseType;
        }> => {
          return await instances.main.put(
            '/sign-up-prospect/other-criteria',
            payload
          );
        },

        getCampaignHelpeeSignUpData: async (
          payload: SignUpProspectGetQualificationRequestType
        ): Promise<{
          data: SignUpProspectGetQualificationResponseType;
        }> => {
          return await instances.main.get(
            `/sign-up-prospect/qualification?prospectUuid=${payload.prospectUuid}`
          );
        },

        sendExpectationSignUpHelpee: async (
          payload: SignUpProspectUpdateExpectationRequestType
        ): Promise<{
          data: SignUpProspectUpdateExpectationResponseType;
        }> => {
          return await instances.main.post(
            '/sign-up-prospect/expectation',
            payload
          );
        },

        sendTouchPointsSignUpHelpee: async (
          payload: SignUpProspectUpdateTouchPointsRequestType
        ): Promise<{
          data: SignUpProspectUpdateTouchPointsResponseType;
        }> => {
          return await instances.main.post(
            '/sign-up-prospect/touch-points',
            payload
          );
        },
      },

      referral: {
        getReferredSignUpData: async (
          payload: SignUpProspectGetReferredRequestType
        ): Promise<{
          data: SignUpProspectGetReferredResponseType;
        }> => {
          return await instances.main.get(
            `/sign-up-prospect/referred?referredUuid=${payload.referredUuid}`
          );
        },

        getReferredSharingSignUpData: async (
          payload: SharingGetSharingDataRequestType
        ): Promise<{
          data: SharingGetSharingDataResponseType;
        }> => {
          return await instances.main.get(
            `/sharing?shareId=${payload.shareId}`
          );
        },

        sendReferredSignUpData: async (
          payload: SignUpProspectPostReferredRequestType,
          turnstileToken?: string
        ): Promise<{
          data: SignUpProspectPostReferredResponseType;
        }> => {
          return await instances.main.post(
            '/sign-up-prospect/referred',
            payload,
            {
              ...(turnstileToken && {
                headers: {
                  'x-turnstile-token': turnstileToken,
                },
              }),
            }
          );
        },

        sendReferredSharingSignUpData: async (
          payload: SignUpProspectPostSharingRequestType
        ): Promise<{
          data: SignUpProspectPostSharingResponseType;
        }> => {
          return await instances.main.post(
            '/sign-up-prospect/sharing',
            payload
          );
        },
      },

      test: {
        testError: async (): Promise<any> => {
          return await instances.main.get('/error/random');
        },
      },
    };
  }

  public static userDashboard() {
    const userStore = useUserStore();

    return {
      auth: {
        getJwtToken: async (
          payload:
            | UserDashboardJwtTokenRequestType
            | ProspectDashboardGetJwtTokenRequestType,
          isProspect: boolean
        ): Promise<{
          data:
            | UserDashboardJwtTokenResponseType
            | ProspectDashboardGetJwtTokenResponseType;
        }> => {
          // Add cache-buster to prevent Chrome from caching/hanging on repeated requests
          const cacheBuster = { _t: Date.now() };

          if (isProspect) {
            return await instances.prospectDashboard.get('/auth/jwt-token', {
              params: { ...payload, ...cacheBuster },
            });
          }

          return await instances.userDashboard.get('/auth/jwt-token', {
            params: { ...payload, ...cacheBuster },
          });
        },

        getSignInData: async (
          organizationSubdomainName: string,
          campaignUuid: string
        ): Promise<any> => {
          let params = '';

          if (campaignUuid) {
            params += `campaignUuid=${campaignUuid}`;
          } else if (organizationSubdomainName) {
            params += `organizationSubdomainName=${organizationSubdomainName}`;
          }

          try {
            return await instances.userDashboard.get(
              `/public/sign-in?${params}`
            );
          } catch (e) {
            return null;
          }
        },

        signIn: async (
          payload: GenericLoginGetRequestType
        ): Promise<{
          data: GenericLoginGetResponseType;
        }> => {
          return await instances.userDashboard.get(
            `/auth/login?email=${encodeURIComponent(payload.email)}`
          );
        },

        getHelpeeSignUpData: async (payload: {
          campaignUuid?: string;
          organizationUuid?: string;
        }): Promise<HelpeeSignupFormData> => {
          let params = '';

          if (payload.campaignUuid) {
            params += `campaignUuid=${payload.campaignUuid}`;
          } else if (payload.organizationUuid) {
            params += `organizationUuid=${payload.organizationUuid}`;
          }
          return await instances.userDashboard.get(
            `/public/sign-up-prospect-form?${params}`
          );
        },
      },

      campaign: {
        getAllCampaigns: async (): Promise<{
          data: UserDashboardGetHelperCampaignsResponseType;
        }> => {
          return await instances.userDashboard.get('/campaigns/helper');
        },

        getCampaigns: async (): Promise<{
          data: UserDashboardGetHelperSignedUpResponseType;
        }> => {
          return await instances.userDashboard.get(
            '/campaigns/helper/signed-up'
          );
        },

        getCampaignInfoHelper: async (
          relationId: number,
          isProspect = false
        ): Promise<{
          data:
            | UserDashboardGetCampaignInfoResponseType
            | ProspectDashboardGetCampaignInfoResponseType;
        }> => {
          if (isProspect) {
            if (!relationId || Number.isNaN(relationId)) {
              throw new Error('relationId is required to fetch campaign info');
            }

            return await instances.prospectDashboard.get('/campaigns/info', {
              params: {
                relationId,
              },
            });
          } else {
            if (!relationId || Number.isNaN(relationId)) {
              throw new Error('relationId is required to fetch campaign info');
            }

            return await instances.userDashboard.get('/campaigns/info', {
              params: {
                relationId,
              },
            });
          }
        },

        getCampaignInfoProspect: async (
          relationId: number
        ): Promise<{
          data: ProspectDashboardGetCampaignInfoResponseType;
        }> => {
          if (!relationId || Number.isNaN(relationId)) {
            throw new Error('relationId is required to fetch campaign info');
          }

          return await instances.prospectDashboard.get('/campaigns/info', {
            params: {
              relationId,
            },
          });
        },

        updateCampaign: async (
          payload: UserDashboardUpdateCampaignHelperSettingsRequestType
        ): Promise<{
          data: UserDashboardUpdateCampaignHelperSettingsResponseType;
        }> => {
          return await instances.userDashboard.put(
            '/campaign-helper/settings',
            payload
          );
        },

        joinCampaign: async (
          payload: UserDashboardJoinCampaignHelperRequestType
        ): Promise<{
          data: UserDashboardJoinCampaignHelperResponseType;
        }> => {
          return await instances.userDashboard.post(
            '/campaign-helper/join',
            payload
          );
        },

        leaveCampaign: async (
          payload: UserDashboardLeaveCampaignHelperRequestType
        ): Promise<{
          data: UserDashboardLeaveCampaignHelperResponseType;
        }> => {
          return await instances.userDashboard.post(
            '/campaign-helper/leave',
            payload
          );
        },
      },

      challenges: {
        getChallenges: async (): Promise<{
          data: UserDashboardGetAllChallengesResponseType;
        }> => {
          return await instances.userDashboard.get('/challenges');
        },

        startChallenge: async (
          payload: UserDashboardStartChallengeRequestType
        ): Promise<{
          data: UserDashboardStartChallengeResponseType;
        }> => {
          return await instances.userDashboard.post(
            `/challenges/start/${payload.challengeId}`
          );
        },

        accomplishChallenge: async (
          payload: UserDashboardValidateChallengeRequestType
        ): Promise<{
          data: UserDashboardValidateChallengeResponseType;
        }> => {
          return await instances.userDashboard.post(
            `/challenges/done/helper/${payload.userChallengeId}`
          );
        },
      },

      events: {
        getEvents: async (): Promise<{
          data: UserDashboardGetAllEventsResponseType;
        }> => {
          return await instances.userDashboard.get('/events');
        },
      },

      ugcs: {
        getUgcs: async (): Promise<{
          data: UserDashboardGetAllUgcsResponseType;
        }> => {
          return await instances.userDashboard.get('/ugcs');
        },

        startUgc: async (
          payload: UserDashboardStartUgcRequestType
        ): Promise<{
          data: UserDashboardStartUgcResponseType;
        }> => {
          return await instances.userDashboard.post(
            `/ugcs/start/${payload.ugcId}`
          );
        },

        accomplishUgc: async (
          payload: UserDashboardValidateUgcRequestType
        ): Promise<{
          data: UserDashboardValidateUgcResponseType;
        }> => {
          return await instances.userDashboard.post(
            `/ugcs/done/helper/${payload.userUgcId}`
          );
        },
      },

      relations: {
        helpee: {
          getRelation: async (
            payload?: ProspectDashboardGetRelationRequestType
          ): Promise<{
            data: ProspectDashboardGetRelationResponseType;
          }> => {
            return await instances.prospectDashboard.get(
              `/relation${
                payload?.relationId ? '?relationId=' + payload.relationId : ''
              }`
            );
          },

          markAsDone: async (
            payload: ProspectDashboardPostRelationMomentDoneRequestType & {
              file?: File;
            }
          ): Promise<{
            data: ProspectDashboardPostRelationMomentDoneResponseType;
          }> => {
            return await instances.prospectDashboard.post(
              '/relation-moments/done',
              serializeFormData(payload),
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
          },

          markAsongoing: async (
            payload: ProspectDashboardPostRelationMomentOngoingRequestType
          ): Promise<{
            data: ProspectDashboardPostRelationMomentOngoingResponseType;
          }> => {
            return await instances.prospectDashboard.post(
              '/relation-moments/ongoing',
              payload
            );
          },

          reportHelper: async (payload: {
            report: any; // Should be changed one day,
            reportScreenshot?: File | null;
          }): Promise<{
            data: ProspectDashboardPostRelationReportResponseType;
          }> => {
            return await instances.prospectDashboard.post(
              '/relation/report',
              {
                reportScreenshot: payload.reportScreenshot,
                report: JSON.stringify(payload.report),
              },
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
          },
        },

        helper: {
          contactAdmin: async (
            payload: UserDashboardRelationMomentPostMessageRequestType
          ): Promise<{
            data: UserDashboardRelationMomentPostMessageResponseType;
          }> => {
            return await instances.userDashboard.post(
              '/relation-moments/helper/message',
              serializeFormData(payload),
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
          },

          getRelation: async (
            payload: UserDashboardRelationsGetHelperRelationDetailsRequestType
          ): Promise<{
            data: UserDashboardRelationsGetHelperRelationDetailsResponseType;
          }> => {
            return await instances.userDashboard.get(
              `/relations/helper/${payload.relationId}`
            );
          },

          getRelations: async (): Promise<{
            data: UserDashboardRelationsGetHelperRelationsResponseType;
          }> => {
            return await instances.userDashboard.get('/relations/helper');
          },

          markAsDone: async (
            payload: UserDashboardRelationMomentPostDoneRequestType & {
              file?: File;
            }
          ): Promise<{
            data: UserDashboardRelationMomentPostDoneResponseType;
          }> => {
            return await instances.userDashboard.post(
              '/relation-moments/done',
              serializeFormData(payload),
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
          },

          markAsongoing: async (
            payload: UserDashboardRelationMomentPostOngoingRequestType
          ): Promise<{
            data: UserDashboardRelationMomentPostOngoingResponseType;
          }> => {
            return await instances.userDashboard.post(
              '/relation-moments/ongoing',
              { relationMomentId: Number(payload.relationMomentId) }
            );
          },

          markAsRead: async (
            payload: UserDashboardRelationMomentPostReadRequestType
          ): Promise<{
            data: UserDashboardRelationMomentPostReadResponseType;
          }> => {
            return await instances.userDashboard.post(
              '/relation-moments/read',
              { relationMomentId: Number(payload.relationMomentId) }
            );
          },

          reportHelpee: async (
            payload: UserDashboardRelationsPostHelperReportHelpeeRequestType & {
              reportScreenshot?: File | null;
            }
          ): Promise<{
            data: UserDashboardRelationsPostHelperReportHelpeeResponseType;
          }> => {
            return await instances.userDashboard.post(
              '/relations/helper/report/helpee',
              {
                reportScreenshot: payload.reportScreenshot,
                report: JSON.stringify(payload.report),
              },
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
          },

          referHelpee: async (
            payload: UserDashboardReferSomeonePostRequestType
          ): Promise<{
            data: UserDashboardReferSomeonePostResponseType;
          }> => {
            return await instances.userDashboard.post('/refer/helpee', payload);
          },
        },
      },

      rewards: {
        getRewards: async (): Promise<{
          data: UserDashboardGetRewardsResponseType;
        }> => {
          return await instances.userDashboard.get('/rewards');
        },

        getPurchasedRewards: async (): Promise<{
          data: UserDashboardGetPurchasedRewardsResponseType;
        }> => {
          return await instances.userDashboard.get('/rewards/purchased');
        },

        purchaseReward: async (
          payload: UserDashboardPurchaseRewardRequestType
        ): Promise<{
          data: UserDashboardPurchaseRewardResponseType;
        }> => {
          return await instances.userDashboard.post(
            `/rewards/${payload.rewardId}`
          );
        },
      },

      user: {
        getUser: async (): Promise<{
          data: UserDashboardGetUserResponseType;
        }> => {
          return await instances.userDashboard.get('/user');
        },

        uploadProfilePicture: async (
          payload: UserDashboardUpdateUserProfilePictureRequestType
        ): Promise<{
          data: UserDashboardUpdateUserProfilePictureResponseType;
        }> => {
          return await instances.userDashboard.put(
            '/user/profile-picture',
            payload,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
        },

        updateUser: async (
          payload: UserDashboardUpdateUserRequestType
        ): Promise<{
          data: UserDashboardUpdateUserResponseType;
        }> => {
          return await instances.userDashboard.put('/user', payload);
        },

        getUserCriteria: async (): Promise<{
          data: UserDashboardGetUserCriteriaResponseType;
        }> => {
          return await instances.userDashboard.get('/campaigns/user/criteria');
        },

        updateUserCriteria: async (
          payload: UserDashboardUpdateUserCriteriaRequestType
        ): Promise<{
          data: UserDashboardUpdateUserCriteriaResponseType;
        }> => {
          return await instances.userDashboard.put('/user-criteria', payload);
        },

        getUserTouchpoints: async (): Promise<{
          data: UserDashboardGetUserTouchPointsResponseType;
        }> => {
          return await instances.userDashboard.get(
            '/campaigns/user/touch-points'
          );
        },

        getHelpeeTouchpoints: async (): Promise<{
          data: ProspectDashboardGetProspectTouchPointsResponseType;
        }> => {
          return await instances.prospectDashboard.get('/prospect-touchPoints');
        },

        updateUserTouchpoint: async (
          payload: UserDashboardUpdateUserTouchPointRequestType
        ): Promise<{
          data: UserDashboardUpdateUserTouchPointResponseType;
        }> => {
          return await instances.userDashboard.put(
            '/user-touchPoint',
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );
        },

        updateProspectTouchpoint: async (
          payload: ProspectDashboardPutProspectTouchPointsRequestType
        ): Promise<{
          data: ProspectDashboardPutProspectTouchPointsResponseType;
        }> => {
          return await instances.prospectDashboard.put(
            '/prospect-touchPoint',
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );
        },

        getSupportData: async (
          relationId?: number,
          campaignId?: number
        ): Promise<{
          data:
            | UserDashboardGetAdminResponseType
            | ProspectDashboardGetSupportAdminResponseType;
        }> => {
          const params: { relationId?: number; campaignId?: number } = {};
          if (relationId !== undefined) params.relationId = relationId;
          if (campaignId !== undefined) params.campaignId = campaignId;

          if (userStore.isProspect) {
            return await instances.prospectDashboard.get('/support/admin', {
              params,
            });
          } else {
            return await instances.userDashboard.get('/support/admin', {
              params,
            });
          }
        },

        sendSupportMessage: async (
          payload:
            | UserDashboardPostMessageRequestType
            | ProspectDashboardPostSupportMessageRequestType
        ): Promise<{
          data:
            | UserDashboardPostMessageResponseType
            | ProspectDashboardPostSupportMessageResponseType;
        }> => {
          if (userStore.isProspect) {
            return await instances.prospectDashboard.post(
              '/support/message',
              payload
            );
          } else {
            return await instances.userDashboard.post(
              '/support/message',
              payload
            );
          }
        },

        getSharingLink: async (): Promise<{
          data: UserDashboardGetSharingResponseType;
        }> => {
          return await instances.userDashboard.get('/sharing');
        },
      },

      reviews: {
        getReviewStatus: async (
          payload:
            | ReviewsGetReviewStatusRequestType
            | ReviewsGetPublicReviewStatusRequestType,
          isConnected: boolean
        ): Promise<{
          data:
            | ReviewsGetReviewStatusResponseType
            | ReviewsGetPublicReviewStatusResponseType;
        }> => {
          return await instances.reviews.get(
            `/reviews/status/${payload.uuid}${isConnected ? '' : '/public'}`
          );
        },

        submitConnectedReview: async (
          payload: ReviewsCreateReviewRequestType
        ): Promise<{
          data: ReviewsCreateReviewResponseType;
        }> => {
          return await instances.reviews.post(
            `/reviews/${payload.uuid}`,
            payload
          );
        },

        submitPublicReview: async (
          payload: ReviewsCreatePublicReviewRequestType & {
            profilePicture: File | null;
          }
        ): Promise<{
          data: ReviewsCreatePublicReviewResponseType;
        }> => {
          const formData = new FormData();
          formData.append('uuid', payload.uuid);
          formData.append('comment', payload.comment);
          formData.append('rating', payload.rating.toString());
          formData.append('firstname', payload.firstname);
          formData.append('lastname', payload.lastname);
          formData.append('email', payload.email);
          formData.append('position', payload.position || '');
          payload.profilePicture &&
            formData.append('profilePicture', payload.profilePicture);
          payload.profilePictureUrl &&
            formData.append('profilePictureUrl', payload.profilePictureUrl);

          return await instances.reviews.post(
            `/reviews/${payload.uuid}/public`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
        },
      },
    };
  }
}
